import { categoriaEfetiva, lineIsConsistent, valorEfetivo, valoresDaLinha } from '@domain/receipt'
import type { ReceiptItem } from '@domain/receipt'
import type { Product } from '@domain/product'
import type { Category } from '@domain/finance'
import type { ReceiptItemPatch } from '@domain/ports/receipt-repository'
import { formatBRL } from '@shared/money/format'
import { SeletorDeCategoria } from '@ui/finance/SeletorDeCategoria'
import { Miniatura } from '@ui/produtos/Miniatura'
import { IconeCheque, IconeLapis, IconeReticencias } from '@ui/shell/icones'
import { fraseDaDivergencia, nomeDaLinha, produtoDaLinha, rastroDaDivergencia } from './linha'
import './nota.css'

/**
 * Uma linha da nota na revisão.
 *
 * ## O nome em destaque, e o texto cru logo embaixo
 *
 * O destaque é o nome que a pessoa reconhece: o que ela deu à linha, ou o do
 * produto, ou — sem nenhum dos dois — o próprio texto lido. **O `rawText`
 * continua visível sempre**, pequeno e em fonte mono, porque é a evidência
 * comparada com o papel (ver o cabeçalho de `Nota.tsx`); só não se repete
 * quando ele já é o destaque.
 *
 * ## A foto é do produto, e só do produto que a linha TEM
 *
 * Sugerido ou confirmado. A linha pulada ou que não existe não tem produto, e
 * a foto de um vínculo que a pessoa recusou seria afirmar o que ela negou.
 *
 * ## Duas ações à vista, o resto no "⋯"
 *
 * Aceitar a sugestão e corrigir a leitura são o trabalho de quase toda linha;
 * trocar o produto, pular e "não existe" são exceção, e moram em
 * `AcoesDaLinha`. Os ícones são botões nus da classe `alvo`, como os de editar
 * e apagar da discussão: 44 px de toque em volta de um desenho de 24.
 *
 * ## A categoria usa o seletor de tela cheia
 *
 * Era o `<select>` nativo, que a decisão 250(b) deixava nas linhas. Ele tinha
 * cara de formulário no meio de cada item, e o seletor próprio tem busca. O
 * painel fecha ao escolher e a lista continua embaixo na mesma altura — ver a
 * decisão 286.
 */
export function LinhaDaNota({
  linha,
  produto,
  categorias,
  categoriaDaNota,
  somenteLeitura,
  onDecidir,
  onCriouCategoria,
  onEditar,
  onMaisAcoes,
}: {
  linha: ReceiptItem
  produto: Product | null
  categorias: Category[]
  /** O piso, quando nem a linha nem o produto dizem. Ver `categoriaEfetiva`. */
  categoriaDaNota: string | null
  somenteLeitura: boolean
  onDecidir: (patch: ReceiptItemPatch) => void
  onCriouCategoria: () => void
  onEditar: () => void
  onMaisAcoes: () => void
}) {
  // **Confirmada, a linha aparece como foi GRAVADA** — decisão 309. A sugestão
  // que ninguém aceitou entra sem produto (só linha confirmada leva produto à
  // Function), e a tela continuava mostrando o nome, a foto e "sugerido" numa
  // nota que já não muda.
  const produtoEfetivo = somenteLeitura && linha.matchStatus === 'pending' ? null : produto
  const vinculado = produtoDaLinha(linha, produtoEfetivo)
  // A divergente pendente pede conferência; a aceita mesmo assim guarda o
  // rastro. Decisão 308.
  const divergencia =
    linha.matchStatus === 'pending' ? fraseDaDivergencia(linha, vinculado) : null
  const rastro = linha.matchStatus === 'confirmed' ? rastroDaDivergencia(linha, vinculado) : null
  // Espera alguém: a sugestão ainda não decidida. A sem produto já entra na
  // soma e não pede nada.
  const aConferir = !somenteLeitura && linha.matchStatus === 'pending' && vinculado !== null
  const nome = nomeDaLinha(linha, produtoEfetivo)
  const valores = valoresDaLinha(linha)
  const fecha = lineIsConsistent(valores)
  const rejeitada = linha.matchStatus === 'rejected'
  const categoriaId = categoriaEfetiva(linha, produto, categoriaDaNota)
  const categoria = categorias.find((c) => c.id === categoriaId)?.name ?? 'sem categoria'

  return (
    <li
      id={`linha-${linha.id}`}
      // A linha que espera alguém ganha a faixa de pendência (`nota.css`) — a
      // forma que se acha de relance numa lista longa. Decisão 310.
      data-a-conferir={aConferir ? '' : undefined}
      className="border-b border-[var(--border)] py-3 text-sm last:border-b-0"
    >
      <div className="flex gap-3">
        <Miniatura nome={nome} src={vinculado?.imageUrl ?? null} className="size-12" />

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span
              className={`min-w-0 flex-1 truncate font-medium ${
                rejeitada ? 'text-[var(--text-muted)] line-through' : ''
              }`}
            >
              {nome}
            </span>
            {linha.discountCents > 0 && (
              // O impresso fica RISCADO ao lado do que se pagou, e não some: quem
              // confere o cupom na mão precisa achar os 5,99 do papel na tela.
              <span className="text-xs text-[var(--text-muted)] line-through tabular-nums">
                {formatBRL(valores.totalCents)}
              </span>
            )}
            {/* Fora da soma, o VALOR é o que saiu da conta — riscá-lo só no
                nome deixava os R$ à direita lendo como somados (decisão 311). */}
            <span
              className={`shrink-0 tabular-nums ${
                rejeitada ? 'text-[var(--text-muted)] line-through' : ''
              }`}
            >
              {formatBRL(valorEfetivo(linha))}
            </span>
          </div>

          {/* O texto cru, sempre visível. É a evidência. */}
          {nome !== linha.rawText && (
            <p className="truncate font-mono text-xs text-[var(--text-muted)]">{linha.rawText}</p>
          )}

          {divergencia !== null && (
            // As DUAS medidas, e não "tamanho diferente": o lote deixou esta
            // linha de fora e mandou conferir aqui — e conferir exige saber o
            // quê. Decisão 304.
            <p className="mt-0.5 text-xs" style={{ color: 'var(--pendente)' }}>
              {divergencia}
            </p>
          )}
          {rastro !== null && (
            <p className="mt-0.5 text-xs text-[var(--text-muted)]">{rastro}</p>
          )}

          {/* Quantidade × unitário — o que o cupom imprime na linha de preço. Só
              quando diz alguma coisa: com uma unidade e a conta fechando, repetir
              o preço em cada uma de 40 linhas seria ruído. A linha que NÃO fecha
              aparece sempre, e marcada: é o sinal mais barato de leitura errada
              que existe (decisão 284). */}
          {(valores.quantity !== 1 || !fecha) && (
            <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs tabular-nums text-[var(--text-muted)]">
              <span>{`${quantidadeLegivel(valores.quantity, linha.unit)} × ${formatBRL(valores.unitPriceCents)}`}</span>
              {!fecha && (
                <span style={{ color: 'var(--pendente)' }}>não fecha com o total da linha — confira</span>
              )}
            </p>
          )}

          {linha.edited !== null && (
            // O lido ao lado do corrigido: é o que permite desconfiar de uma
            // correção, e é o que a folha de edição promete não apagar.
            <p className="mt-0.5 text-xs text-[var(--text-faint)] tabular-nums">
              {`editado · lido ${quantidadeLegivel(linha.quantity, linha.unit)} × ${formatBRL(linha.unitPriceCents)} = ${formatBRL(linha.totalCents)}`}
            </p>
          )}

          {linha.discountCents > 0 && (
            <p className="mt-0.5 text-xs text-[var(--text-muted)]">
              {`${formatBRL(linha.discountCents)} de desconto`}
            </p>
          )}

          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            {/* A categoria vem ANTES do status, e a ordem é a importância: é ela
                que move dinheiro. A linha rejeitada não tem: ela sai de tudo. */}
            {!rejeitada && (
              <SeletorDeCategoria
                categorias={categorias}
                value={categoriaId}
                rotulo={`Categoria de ${linha.rawText}: ${categoria}`}
                disabled={somenteLeitura}
                className="max-w-[60%]"
                onChange={(categoryId) => {
                  onDecidir({ categoryId })
                }}
                onCriou={onCriouCategoria}
              />
            )}
            <Selo linha={linha} temProduto={vinculado !== null} />

            {!somenteLeitura && (
              // O recuo negativo alinha o último ícone com o valor acima: o
              // `alvo` é uma caixa de 44 px em volta de um desenho de 24.
              <span className="fileira-de-icones -mr-2.5 ml-auto text-[var(--text-muted)]">
                {linha.matchStatus === 'pending' && vinculado !== null && divergencia === null && (
                  <button
                    type="button"
                    className="alvo"
                    data-acao-principal
                    aria-label={`Aceitar ${vinculado.name}`}
                    title={`Aceitar ${vinculado.name}`}
                    onClick={() => {
                      onDecidir({ matchStatus: 'confirmed' })
                    }}
                  >
                    <IconeCheque />
                  </button>
                )}
                {divergencia !== null && (
                  // **Palavra, e não o ✓** — decisão 308. Um toque no ✓ aceitava
                  // a lata de 350ml para um cupom de 310ML, a 8px do lápis, e o
                  // aviso sumia junto. Aqui o toque abre as três saídas, com as
                  // duas medidas à vista.
                  <button
                    type="button"
                    className="alvo rounded-full border px-3 text-xs font-medium"
                    style={{ borderColor: 'var(--pendente)', color: 'var(--pendente)' }}
                    data-acao-principal
                    aria-label={`Conferir ${linha.rawText}`}
                    onClick={onMaisAcoes}
                  >
                    Conferir
                  </button>
                )}
                <button
                  type="button"
                  className="alvo"
                  aria-label={`Editar ${linha.rawText}`}
                  // Na nota, o gesto de cada linha é corrigir: ↑↓ andam pelo
                  // lápis, e Enter abre a folha de edição.
                  data-nav-linha
                  title="Corrigir nome e valores"
                  onClick={onEditar}
                >
                  <IconeLapis />
                </button>
                <button
                  type="button"
                  className="alvo"
                  aria-label={`Mais ações de ${linha.rawText}`}
                  title="Trocar produto, pular, não existe"
                  onClick={onMaisAcoes}
                >
                  <IconeReticencias />
                </button>
              </span>
            )}
          </div>
        </div>
      </div>
    </li>
  )
}

/**
 * O estado da decisão, curto. A explicação longa de "pular" e "não existe"
 * mora nas ações, onde a escolha é feita; aqui ela fica no `title`.
 */
function Selo({ linha, temProduto }: { linha: ReceiptItem; temProduto: boolean }) {
  // A manual é `skipped` no banco, e "pulado — sem produto" descreveria mal o
  // que ela é: ninguém pulou nada, alguém acrescentou. E o abatimento merece
  // nome próprio, porque é a única linha que DIMINUI a nota.
  if (linha.manual) {
    const abate = linha.totalCents < 0
    return (
      <SeloPintado
        cor={abate ? 'var(--economia)' : 'var(--text-faint)'}
        texto={abate ? 'abatimento' : 'acrescentado'}
        explicacao={
          abate
            ? 'Acrescentado à mão — diminui o total da nota'
            : 'Acrescentado à mão — entra na soma da nota'
        }
      />
    )
  }

  // **A consequência na soma, e não o nome interno** — decisões 306 e 309.
  // "Pulado"
  // e "não existe" eram os nomes de `skipped` e `rejected`, e para quem nunca
  // leu a especificação soavam iguais: "pulado" lê como "tirado da conta",
  // que é o oposto do que faz. A linha passou a dizer o que faz com o
  // dinheiro; os nomes internos ficaram no código e no vault.
  //
  // **Sem produto lê igual, pendente ou pulada.** Desde a 304 as duas gravam
  // a mesma coisa na confirmação, e duas palavras ensinariam uma diferença
  // que não existe.
  //
  // **E cada cor faz um trabalho só** (DESIGN.md). "Confirmado" usava o teal
  // da economia — que quer dizer "compensa" — e "não existe" o carmim de "saiu
  // dinheiro". O resolvido é quieto; amarelo é só o que espera alguém.
  const semProduto = {
    texto: 'sem produto · entra na soma',
    explicacao: 'Não vira produto do catálogo, e o valor continua na soma',
    cor: 'var(--text-faint)',
  }
  const { texto, explicacao, cor } = {
    pending: temProduto
      ? { texto: 'sugerido', explicacao: 'Produto sugerido pela leitura — confira e aceite', cor: 'var(--pendente)' }
      : semProduto,
    confirmed: { texto: 'confirmado', explicacao: 'Produto aceito', cor: 'var(--text-muted)' },
    skipped: semProduto,
    rejected: {
      texto: 'fora da soma',
      explicacao: 'A leitura inventou esta linha — ela não entra na soma',
      cor: 'var(--text-faint)',
    },
  }[linha.matchStatus]

  return (
    <SeloPintado
      cor={cor}
      texto={texto}
      explicacao={explicacao}
      realce={linha.matchStatus === 'pending' && temProduto}
    />
  )
}

function SeloPintado({
  cor,
  texto,
  explicacao,
  realce = false,
}: {
  cor: string
  texto: string
  explicacao: string
  /** A palavra na cor do estado, e não apagada: só no que espera alguém. */
  realce?: boolean
}) {
  return (
    <span
      title={explicacao}
      className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)]"
      style={realce ? { color: cor } : undefined}
    >
      <span aria-hidden="true" className="size-1.5 rounded-full" style={{ background: cor }} />
      {texto}
    </span>
  )
}

/**
 * A quantidade como o cupom a imprime, sem os zeros: `2` → "2", `0.426` com
 * unidade KG → "0,426 kg". A unidade só aparece quando muda a leitura: "2 un ×"
 * não diz mais que "2 ×", e "0,426 ×" sem o kg não diz o que é.
 */
function quantidadeLegivel(quantidade: number, unit: string | null): string {
  const numero = String(quantidade).replace('.', ',')
  const unidade = unit?.trim().toLowerCase() ?? ''
  return ['kg', 'g', 'l', 'ml'].includes(unidade) ? `${numero} ${unidade}` : numero
}
